import React, { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Button, ButtonGroup, Grid, Typography, Box } from "@mui/material";
import { inspectionInfoStorage, SelectedView } from "../../services/ocr/InspectionInfoStorageModel";
import { translationService } from "../../services/translation/TranslationService";
import PipeDiagram from "./PipeDiagram";
import { OcrService } from "../../services/ocr/OcrService";
import InspectionGrid from "./InspectionGrid";
import { mainCodeColorStorage } from "../../services/mc-colors/MainCodeColorStorage";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import DetectionLegend, { LegendCode } from "./DetectionLegend";
import { activeIndexByTime, ocrTsToSeconds } from "./playbackSync";
import { REVIEWED_PIPE_COLOR, reviewedFrameSx, reviewedGridSx, ReviewedChip } from "./reviewedGraphStyle";
import './Inspection.scss';

interface IInspectionTableProps {
  inspectionInsightStorage: typeof inspectionInfoStorage;
  ocrService: OcrService;
  /** Drawn from the reviewed data (Review Workbench) rather than the machine output. */
  reviewed?: boolean;
};

export const InspectionTable = observer(
  ({ inspectionInsightStorage, ocrService, reviewed }: IInspectionTableProps) => {
    const [hidden, setHidden] = useState<Set<string>>(new Set());

    const pipeLength = ocrService.mainInformation.find(
      (item: { key: string }) => item.key.toLowerCase() === "rohrlänge" || item.key.toLowerCase() === "rohrlange"
    );
    const pipeLengthValue = pipeLength ? parseFloat(pipeLength.value) : 60;
    const mainCodeColors = mainCodeColorStorage.colors;
    const colorFor = (code: string) =>
      mainCodeColors.find((c) => c.mainCode === code)?.color ?? "gray";

    // Distinct main codes present (in detection order), for the legend toggles.
    const legendCodes: LegendCode[] = useMemo(() => {
      const seen = new Map<string, string>();
      inspectionInsightStorage.activeDetections.forEach((d: any) => {
        if (d.main_Code && !seen.has(d.main_Code)) seen.set(d.main_Code, colorFor(d.main_Code));
      });
      return Array.from(seen, ([mainCode, color]) => ({ mainCode, color }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inspectionInsightStorage.activeDetections, mainCodeColors]);

    const toggle = (code: string) =>
      setHidden((prev) => {
        const next = new Set(prev);
        next.has(code) ? next.delete(code) : next.add(code);
        return next;
      });

    // Filter rows + the pipe arrays together so they stay index-aligned.
    const baseRows =
      inspectionInsightStorage.view === SelectedView.Combined
        ? inspectionInsightStorage.combinedRows
        : inspectionInsightStorage.rows;
    const filteredRows = baseRows.filter((r: any) => !hidden.has(r.main_Code));

    const distance = filteredRows.map((r: any) => r.distance);
    const mainCodes = filteredRows.map((r: any) => r.main_Code);

    // The detection currently playing (by video time) and the user's clicked row,
    // both mapped into the filtered list so the pipe + grid agree.
    const activeIndex = activeIndexByTime(
      filteredRows.map((r: any) => ocrTsToSeconds(r.timestamp)),
      videoPlayerService.videoProgress
    );
    const selectedIndex = (() => {
      if (inspectionInsightStorage.selectedIndex == null) return null;
      const i = filteredRows.findIndex(
        (r: any) => r.id === `row-${inspectionInsightStorage.selectedIndex}`
      );
      return i >= 0 ? i : null;
    })();

    return (
      <Box className="base-data-table" sx={reviewed ? reviewedFrameSx : undefined}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ textAlign: "left", display: "block" }}>
              {translationService.translate("ocrService.inspectionInfo.table-name")}
            </Typography>
            {reviewed && <ReviewedChip />}
          </Box>
          <ButtonGroup variant="outlined" data-html2canvas-ignore>
            <Button
              onClick={() => inspectionInsightStorage.setView(SelectedView.Detected)}
              variant={
                inspectionInsightStorage.view === SelectedView.Detected ? "contained" : "outlined"
              }
            >
              {translationService.translate("ocrService.inspectionInfo.button-detectedText")}
            </Button>
            <Button
              onClick={() => inspectionInsightStorage.setView(SelectedView.Extracted)}
              variant={
                inspectionInsightStorage.view === SelectedView.Extracted ? "contained" : "outlined"
              }
            >
              {translationService.translate("ocrService.inspectionInfo.button-extractedInsights")}
            </Button>
            <Button
              onClick={() => inspectionInsightStorage.setView(SelectedView.Combined)}
              variant={
                inspectionInsightStorage.view === SelectedView.Combined ? "contained" : "outlined"
              }
            >
              {translationService.translate("ocrService.inspectionInfo.button-showBoth")}
            </Button>
          </ButtonGroup>
        </Box>

        <DetectionLegend
          codes={legendCodes}
          hidden={hidden}
          onToggle={toggle}
          onShowAll={() => setHidden(new Set())}
        />

        <Grid
          container
          direction="row"
          spacing={0}
          sx={{ position: "relative", overflow: "hidden", flexWrap: "nowrap", height: "100%" }}
        >
          <Grid item sm="auto" sx={{ position: "sticky", left: 0, zIndex: 1 }}>
            <PipeDiagram
              distance={distance}
              pipeLengthValue={pipeLengthValue ?? 0}
              view={inspectionInsightStorage.view}
              selectedIndex={selectedIndex}
              activeIndex={activeIndex}
              mainCodes={mainCodes}
              mainCodeColors={mainCodeColors}
              pipeColor={reviewed ? REVIEWED_PIPE_COLOR : undefined}
            />
          </Grid>

          <div style={{ display: 'flex', flexGrow: 1,  overflow: 'auto', height: '100%' }}>
          <InspectionGrid
            rows={filteredRows}
            columns={inspectionInsightStorage.columns}
            columnGroups={inspectionInsightStorage.view === SelectedView.Combined ? inspectionInsightStorage.combinedColumnGroup : []}
            activeIndex={activeIndex}
            // Follow the playback head, but only while playing (don't fight manual scroll).
            followActive={videoPlayerService.isVideoPlaying}
            sx={reviewed ? reviewedGridSx : undefined}
          />
          </div>
        </Grid>
      </Box>
    );
  }
);
